import { JSX, Show, createSignal } from 'solid-js';
import { A } from '@solidjs/router';

export function PageHeader(props: { crumbs?: { href?: string; label: string }[]; title: string; actions?: JSX.Element }) {
  return (
    <div class="mb-6 flex flex-col gap-2">
      <Show when={props.crumbs?.length}>
        <nav aria-label="Breadcrumb" class="flex flex-wrap items-center gap-2 text-sm text-ink-3">
          {props.crumbs!.map((c, i) => (
            <>
              {i > 0 && <span aria-hidden="true">/</span>}
              {c.href ? <A href={c.href} class="hover:text-magenta">{c.label}</A> : <span>{c.label}</span>}
            </>
          ))}
        </nav>
      </Show>
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="wordmark text-3xl">{props.title}</h1>
        <div class="ml-auto flex flex-wrap gap-2">{props.actions}</div>
      </div>
    </div>
  );
}

export function StatusPill(props: { tone: 'ok' | 'warn' | 'off' | 'bad'; children: JSX.Element }) {
  // A function, not a const: Solid components run once, so props must be read inside JSX to stay reactive.
  const dot = () => ({ ok: 'bg-success', warn: 'bg-warning', off: 'bg-mist', bad: 'bg-error' })[props.tone];
  return (
    <span class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line px-2.5 py-0.5 text-sm font-semibold text-ink-2">
      <span class={`h-2 w-2 rounded-full ${dot()}`} aria-hidden="true" />{props.children}
    </span>
  );
}

export function ErrorLine(props: { error: () => string }) {
  return <Show when={props.error()}><p role="alert" class="mt-2 text-sm font-semibold text-error">{props.error()}</p></Show>;
}

/** Run an async action with a busy flag and an error message. */
export function useAction() {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError('');
    try { return await fn(); } catch (e: any) { setError(e?.message ?? String(e)); return undefined; } finally { setBusy(false); }
  }
  return { busy, error, run, setError };
}

export function CopyField(props: { value: string; label: string }) {
  const [copied, setCopied] = createSignal(false);
  return (
    <div class="flex items-center gap-2">
      <code class="mono min-w-0 flex-1 truncate rounded-lg bg-static px-3 py-2" title={props.value}>{props.value}</code>
      <button class="btn btn-sm btn-secondary" aria-label={`Copy ${props.label}`}
        onClick={async () => { await navigator.clipboard.writeText(props.value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
        {copied() ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** A destructive action asks inline: the first click shows what will happen and a confirm button. With `typed`,
 *  the confirm button only unlocks once that exact text has been typed (for actions that can't be undone). */
export function ConfirmButton(props: { label: string; warning: string; confirm: string; typed?: string; busy?: boolean;
  class?: string; onConfirm: () => void }) {
  const [open, setOpen] = createSignal(false);
  const [text, setText] = createSignal('');
  const ready = () => !props.typed || text().trim() === props.typed;
  return (
    <Show when={open()} fallback={<button class={props.class ?? 'btn btn-sm btn-ghost text-error'} onClick={() => setOpen(true)}>{props.label}</button>}>
      <div class="flex flex-col gap-2 rounded-xl border-2 border-error/40 bg-static p-3" role="alertdialog" aria-label={props.label}>
        <p class="text-sm">{props.warning}</p>
        <Show when={props.typed}>
          <input class="input input-sm input-bordered" placeholder={`Type ${props.typed} to confirm`} value={text()}
            onInput={(e) => setText(e.currentTarget.value)} aria-label={`Type ${props.typed} to confirm`} />
        </Show>
        <div class="flex gap-2">
          <button class="btn btn-sm btn-error" disabled={!ready() || props.busy} onClick={() => { props.onConfirm(); setOpen(false); setText(''); }}>{props.confirm}</button>
          <button class="btn btn-sm btn-ghost" onClick={() => { setOpen(false); setText(''); }}>Cancel</button>
        </div>
      </div>
    </Show>
  );
}
